// Small, pure formatting helpers for the journey views. Kept separate from the
// tokens (which carry icons) so the canvas and the linear fallback share one
// duration formatter and it stays trivially unit-testable.

/**
 * Human-readable duration from a millisecond span. Sub-second spans render in
 * ms; everything else rolls up to h/m/s (dropping zero leading units). Returns
 * null for missing / non-finite / negative input so callers can omit the label
 * rather than print "NaN" or "-3s".
 */
export function formatDuration(ms: number | null | undefined): string | null {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return null;
  if (ms < 1000) return `${Math.round(ms)}ms`;

  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
  return parts.join(" ");
}

/** Span between two ISO timestamps in ms, or null if either is unparseable. */
export function spanBetween(
  start?: string | null,
  end?: string | null,
): number | null {
  if (!start || !end) return null;
  const startMs = Date.parse(start);
  const endMs = Date.parse(end);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  const span = endMs - startMs;
  return span >= 0 ? span : null;
}
